import PubSub from 'pubsub-js';
import jwt_decode from 'jwt-decode';
import DataApi from '../api/DataApi';

const STORAGE_PREFIX = 'spark_offline_record_saves_';

let syncInProgress = false;
let listenerInitialized = false;

function getStorageKey() {
  if (typeof localStorage === 'undefined') {
    return null;
  }
  try {
    const token = localStorage.getItem('token');
    if (!token || token === 'null') {
      return null;
    }
    const decoded = jwt_decode(token);
    const uid =
      decoded?.id ||
      decoded?.userid ||
      decoded?.userId ||
      decoded?.sub ||
      'anonymous';
    return `${STORAGE_PREFIX}${uid}`;
  } catch {
    return null;
  }
}

function readQueue(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(storageKey, queue) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(queue));
    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('offline-record-queue-changed', {
          detail: { pendingCount: queue.length },
        })
      );
    }
  } catch (e) {
    console.warn('offlineRecordSaveQueue: failed to persist queue', e?.message);
    throw e;
  }
}

function dedupeKey(entry) {
  if (entry.recordId) {
    return `${entry.objectName}:${entry.recordId}`;
  }
  if (entry.clientDraftKey) {
    return `${entry.objectName}:draft:${entry.clientDraftKey}`;
  }
  return `${entry.objectName}:entry:${entry.id}`;
}

export function isBrowserOnline() {
  if (typeof navigator === 'undefined') {
    return true;
  }
  return navigator.onLine !== false;
}

export function isNetworkError(error) {
  if (!error) {
    return false;
  }
  if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED') {
    return true;
  }
  if (error.message === 'Network Error') {
    return true;
  }
  if (!error.response && error.request) {
    return true;
  }
  return false;
}

/**
 * Queue a record create/update for sync when back online.
 * @param {object} entry
 * @returns {object} persisted entry
 */
export function enqueueOfflineRecordSave(entry) {
  const storageKey = getStorageKey();
  if (!storageKey) {
    throw new Error('Not signed in — cannot save offline.');
  }

  const queue = readQueue(storageKey);
  const id =
    entry.id ||
    (typeof crypto !== 'undefined' && crypto.randomUUID
      ? crypto.randomUUID()
      : `offline-${Date.now()}-${Math.random().toString(36).slice(2)}`);

  const normalized = {
    id,
    objectName: entry.objectName,
    recordId: entry.recordId || null,
    clientDraftKey: entry.clientDraftKey || null,
    method: entry.recordId ? 'update' : 'create',
    payload: entry.payload,
    meta: entry.meta || {},
    queuedAt: Date.now(),
  };

  const key = dedupeKey(normalized);
  const next = queue.filter(item => dedupeKey(item) !== key);
  next.push(normalized);
  writeQueue(storageKey, next);
  return normalized;
}

export function getOfflineQueueCount() {
  const storageKey = getStorageKey();
  if (!storageKey) {
    return 0;
  }
  return readQueue(storageKey).length;
}

export function hasPendingOfflineSave(objectName, recordId, clientDraftKey) {
  const storageKey = getStorageKey();
  if (!storageKey) {
    return false;
  }
  const queue = readQueue(storageKey);
  return queue.some(item => {
    if (item.objectName !== objectName) {
      return false;
    }
    if (recordId && item.recordId === recordId) {
      return true;
    }
    if (!recordId && clientDraftKey && item.clientDraftKey === clientDraftKey) {
      return true;
    }
    return false;
  });
}

async function syncOneEntry(entry, idMap) {
  let recordId = entry.recordId;
  if (!recordId && entry.clientDraftKey && idMap[entry.clientDraftKey]) {
    recordId = idMap[entry.clientDraftKey];
  }

  const dataApi = new DataApi(`object/${entry.objectName}`);
  const response = recordId
    ? await dataApi.update(entry.payload, recordId)
    : await dataApi.create(entry.payload);

  const newId =
    response?.data?.id ||
    response?.data?.data?.id ||
    response?.data?.record?.id;

  if (!recordId && entry.clientDraftKey && newId) {
    idMap[entry.clientDraftKey] = newId;
  }

  return {
    entry,
    recordId: recordId || newId || null,
    response,
  };
}

/**
 * Flush queued saves (FIFO). Stops on first failure so order is preserved.
 * @returns {Promise<{ synced: number, failed: number, results: Array, remaining: number }>}
 */
export async function syncOfflineRecordQueue() {
  if (!isBrowserOnline()) {
    return { synced: 0, failed: 0, results: [], remaining: getOfflineQueueCount() };
  }

  const storageKey = getStorageKey();
  if (!storageKey || syncInProgress) {
    return {
      synced: 0,
      failed: 0,
      results: [],
      remaining: getOfflineQueueCount(),
    };
  }

  const queue = readQueue(storageKey);
  if (queue.length === 0) {
    return { synced: 0, failed: 0, results: [], remaining: 0 };
  }

  syncInProgress = true;
  const idMap = {};
  const results = [];
  const remaining = [];
  let synced = 0;
  let failed = 0;
  let stop = false;

  try {
    for (const entry of queue) {
      if (stop) {
        remaining.push(entry);
        continue;
      }
      try {
        const result = await syncOneEntry(entry, idMap);
        results.push(result);
        synced += 1;
      } catch (error) {
        console.error('offlineRecordSaveQueue: sync failed for entry', entry.id, error);
        remaining.push(entry);
        failed += 1;
        stop = true;
      }
    }

    writeQueue(storageKey, remaining);

    if (typeof window !== 'undefined') {
      window.dispatchEvent(
        new CustomEvent('offline-record-queue-changed', {
          detail: { synced, failed, results, pendingCount: remaining.length },
        })
      );
    }

    return { synced, failed, results, remaining: remaining.length };
  } finally {
    syncInProgress = false;
  }
}

/**
 * Register `online` listener and run an initial flush when the app loads.
 */
export function initOfflineRecordSaveSync() {
  if (listenerInitialized || typeof window === 'undefined') {
    return;
  }
  listenerInitialized = true;

  const runSync = async () => {
    if (!getStorageKey() || !isBrowserOnline()) {
      return;
    }
    const outcome = await syncOfflineRecordQueue();
    if (outcome.synced > 0) {
      PubSub.publish('RECORD_SAVED_TOAST', {
        message:
          outcome.synced === 1
            ? '1 offline change synced to the server.'
            : `${outcome.synced} offline changes synced to the server.`,
      });
    }
    if (outcome.failed > 0) {
      PubSub.publish('RECORD_ERROR_TOAST', {
        message:
          'Could not sync an offline save. Your changes are still saved locally and will retry when you are back online.',
      });
    }
  };

  window.addEventListener('online', () => {
    runSync();
  });

  if (isBrowserOnline() && getOfflineQueueCount() > 0) {
    runSync();
  }
}

export function clearOfflineRecordQueue() {
  const storageKey = getStorageKey();
  if (!storageKey) {
    return;
  }
  writeQueue(storageKey, []);
}
